import { useQuery } from '@tanstack/react-query';
import { roleApi, Role } from '@/services/roleApi';
import { teamApi } from '@/services/teamApi';
import { useAuth } from './useAuth';

interface PermissionCheck {
  module: string;
  action: string;
}

// Legacy role permissions for backward compatibility
const getLegacyRolePermissions = (role: string) => {
  const permissions = {
    Owner: [
      { module: 'dashboard', actions: ['read'] },
      { module: 'organizations', actions: ['read', 'update', 'manage'] },
      { module: 'projects', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'users', actions: ['create', 'read', 'update', 'delete', 'invite', 'manage'] },
      { module: 'roles', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'teams', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'invitations', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'text-to-speech', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'speech-to-text', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'audio-translation', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'voices', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'videos', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'video-translation', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'clip-generation', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'video-understanding', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'avatars', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'newsroom', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'settings', actions: ['read', 'update', 'manage'] },
      { module: 'integrations', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'analytics', actions: ['read', 'manage'] },
      { module: 'configurations', actions: ['create', 'read', 'update', 'delete', 'manage'] }
    ],
    Admin: [
      { module: 'dashboard', actions: ['read'] },
      { module: 'organizations', actions: ['read'] },
      { module: 'projects', actions: ['create', 'read', 'update', 'delete', 'manage'] },
      { module: 'users', actions: ['create', 'read', 'update', 'delete', 'invite'] },
      { module: 'roles', actions: ['read'] },
      { module: 'teams', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'invitations', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'text-to-speech', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'speech-to-text', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'audio-translation', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'voices', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'videos', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'video-translation', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'clip-generation', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'video-understanding', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'avatars', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'newsroom', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'settings', actions: ['read', 'update'] },
      { module: 'integrations', actions: ['read'] },
      { module: 'analytics', actions: ['read'] },
      { module: 'configurations', actions: ['read'] }
    ],
    Manager: [
      { module: 'dashboard', actions: ['read'] },
      { module: 'organizations', actions: ['read'] },
      { module: 'projects', actions: ['create', 'read', 'update', 'manage'] },
      { module: 'users', actions: ['read', 'invite'] },
      { module: 'roles', actions: ['read'] },
      { module: 'teams', actions: ['create', 'read', 'update'] },
      { module: 'invitations', actions: ['create', 'read'] },
      { module: 'text-to-speech', actions: ['create', 'read', 'update'] },
      { module: 'speech-to-text', actions: ['create', 'read', 'update'] },
      { module: 'audio-translation', actions: ['create', 'read', 'update'] },
      { module: 'voices', actions: ['create', 'read', 'update'] },
      { module: 'videos', actions: ['create', 'read', 'update'] },
      { module: 'video-translation', actions: ['create', 'read', 'update'] },
      { module: 'clip-generation', actions: ['create', 'read', 'update'] },
      { module: 'video-understanding', actions: ['create', 'read', 'update'] },
      { module: 'avatars', actions: ['create', 'read', 'update'] },
      { module: 'newsroom', actions: ['create', 'read', 'update'] },
      { module: 'settings', actions: ['read'] },
      { module: 'integrations', actions: ['read'] }
    ],
    Member: [
      { module: 'dashboard', actions: ['read'] },
      { module: 'organizations', actions: ['read'] },
      { module: 'projects', actions: ['read', 'update'] },
      { module: 'users', actions: ['read'] },
      { module: 'teams', actions: ['read'] },
      { module: 'text-to-speech', actions: ['read'] },
      { module: 'speech-to-text', actions: ['read'] },
      { module: 'audio-translation', actions: ['read'] },
      { module: 'voices', actions: ['read'] },
      { module: 'videos', actions: ['read'] },
      { module: 'video-translation', actions: ['read'] },
      { module: 'clip-generation', actions: ['read'] },
      { module: 'video-understanding', actions: ['read'] },
      { module: 'avatars', actions: ['read'] },
      { module: 'newsroom', actions: ['read'] },
      { module: 'settings', actions: ['read'] },
      { module: 'integrations', actions: ['read'] },
      { module: 'analytics', actions: ['read'] }
    ],
    Viewer: [
      { module: 'dashboard', actions: ['read'] },
      { module: 'organizations', actions: ['read'] },
      { module: 'projects', actions: ['read'] },
      { module: 'users', actions: ['read'] },
      { module: 'text-to-speech', actions: ['read'] },
      { module: 'speech-to-text', actions: ['read'] },
      { module: 'audio-translation', actions: ['read'] },
      { module: 'voices', actions: ['read'] },
      { module: 'videos', actions: ['read'] },
      { module: 'video-translation', actions: ['read'] },
      { module: 'clip-generation', actions: ['read'] },
      { module: 'video-understanding', actions: ['read'] },
      { module: 'avatars', actions: ['read'] },
      { module: 'newsroom', actions: ['read'] },
      { module: 'integrations', actions: ['read'] },
      { module: 'analytics', actions: ['read'] }
    ]
  };
  
  return permissions[role as keyof typeof permissions] || [];
};

export const usePermissions = () => {
  const { user, activeOrganization } = useAuth();

  // Get roleId from active organization, fallback to legacy roleId
  const currentRoleId = activeOrganization?.roleId || user?.roleId;

  const { data: userRole, isLoading } = useQuery({
    queryKey: ['user-role', currentRoleId],
    queryFn: () => {
      if (!currentRoleId) return null;
      return roleApi.getById(currentRoleId).then(res => res.data.data);
    },
    enabled: !!currentRoleId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Team capability narrowing — a team can only take modules away from what
  // the Role above already grants, never add to it. null = unrestricted.
  // Must mirror the backend's contentPermission.ts so the UI never shows an
  // action the backend will actually reject.
  const { data: capabilities } = useQuery({
    queryKey: ['my-team-capabilities'],
    queryFn: () => teamApi.getMyCapabilities().then(res => res.data.data),
    staleTime: 5 * 60 * 1000,
  });
  const allowedModules = capabilities?.allowedModules ?? null;

  const hasPermission = (module: string, action: string): boolean => {
    let roleAllows: boolean;

    if (!userRole) {
      // Fallback to legacy role system if no roleId
      const currentRole = activeOrganization?.role || user?.role;
      if (currentRole) {
        const legacyPermissions = getLegacyRolePermissions(currentRole);
        const modulePermission = legacyPermissions.find(p => p.module === module);
        roleAllows = modulePermission ? modulePermission.actions.includes(action) : false;
      } else {
        roleAllows = false;
      }
    } else {
      const modulePermission = userRole.permissions.find(p => p.module === module);
      roleAllows = modulePermission ? modulePermission.actions.includes(action) : false;
    }

    if (!roleAllows) return false;
    if (allowedModules === null) return true;
    return allowedModules.includes(module);
  };

  const hasAnyPermission = (checks: PermissionCheck[]): boolean => {
    return checks.some(check => hasPermission(check.module, check.action));
  };

  const hasAllPermissions = (checks: PermissionCheck[]): boolean => {
    return checks.every(check => hasPermission(check.module, check.action));
  };

  const canAccessModule = (module: string): boolean => {
    if (allowedModules !== null && !allowedModules.includes(module)) return false;
    if (!userRole) return false;
    return userRole.permissions.some(p => p.module === module && p.actions.length > 0);
  };

  return {
    userRole,
    isLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessModule,
  };
};
